import type { AnalyticsDestination, AnalyticsTraits, EventProperties } from "@/lib/analytics/analytics.types";
import { isSentinelValue } from "@/lib/env";

declare global {
  interface Window {
    twq?: (...args: unknown[]) => void;
  }
}

const PIXEL_ID = process.env.NEXT_PUBLIC_X_ADS_PIXEL_ID;
const SIGNUP_EVENT_ID = process.env.NEXT_PUBLIC_X_ADS_SIGNUP_EVENT_ID;

const enabled = (): boolean =>
  typeof window !== "undefined" && !!PIXEL_ID && !isSentinelValue(PIXEL_ID) && process.env.NODE_ENV !== "test";

/** Cached identity for conversion payloads (set on identify, cleared on reset). */
let cachedId: string | null = null;
let cachedEmail: string | null = null;

/**
 * Call twq only when the HEAD pixel has installed it. Do not seed a stub —
 * the X loader uses `e.twq||` and would skip injecting uwt.js if we set
 * window.twq first. Signup fires after async ack, so the script is normally
 * ready; early calls no-op.
 *
 * First API call conversions are sent server-side via the Ads Conversions API
 * (see frontend-api XAdsConversionsClient) — not from this destination.
 */
const twq = (...args: unknown[]): void => {
  if (!enabled() || typeof window.twq !== "function") return;
  window.twq(...args);
};

const baseConversionParams = (): {
  value: null;
  conversion_id: string | null;
  email_address: string | null;
} => ({
  value: null,
  conversion_id: cachedId,
  email_address: cachedEmail,
});

export const xDestination: AnalyticsDestination = {
  name: "x",
  isEnabled: enabled,
  pageView: () => {
    // Page views are handled by the base twq('config') in the HEAD script.
  },
  track: (name, _properties?: EventProperties) => {
    if (name !== "sign_up") {
      return;
    }
    if (!SIGNUP_EVENT_ID || isSentinelValue(SIGNUP_EVENT_ID)) return;
    twq("event", SIGNUP_EVENT_ID, baseConversionParams());
  },
  identify: (traits: AnalyticsTraits) => {
    cachedId = typeof traits.id === "string" && traits.id.trim() !== "" ? traits.id : null;
    cachedEmail =
      typeof traits.email === "string" && traits.email.trim() !== "" ? traits.email.trim().toLowerCase() : null;
  },
  reset: () => {
    cachedId = null;
    cachedEmail = null;
  },
};
